import { z } from 'zod'

/**
 * Per-site capability flags reported by the server.
 *
 * Plugins activated on a subsite expose their presence here so the SPA
 * can show / hide whole sections without a feature-detection round-trip.
 * Everything in this block is **optional** — if the backend hasn't
 * surfaced the field yet (or the plugin isn't installed), the SPA
 * treats it as "off". Don't gate critical UX on a positive signal that
 * might not exist; instead, let absent = disabled.
 */
const SiteFeaturesSchema = z
  .object({
    /**
     * AI menu-photo uploader (ez-helper plugin, `ezh/v1/*` namespace).
     * When active, the dashboard surfaces an "AI uploader" tile under
     * Menu that lets the user photograph a paper menu and have the
     * server fill the editor with parsed dish/category previews for
     * review-and-publish. The contract isn't shipped yet — the section
     * is a dev-only skeleton.
     *
     * NOTE: This is a legacy boolean from `/auth/me`. The richer,
     * authoritative source is now `ezqr/v1/site` (capabilities) per
     * memory/site-capabilities-contract.md. Ordering already migrated;
     * this field stays until the AI-uploader plugin is provisioned
     * the same way.
     */
    aiUploader: z
      .object({
        /** Plugin installed AND switched on for this subsite. */
        active: z.boolean(),
      })
      .partial()
      .optional(),
  })
  .partial()
  .optional()

export const SiteSchema = z.object({
  id: z.number().int().positive(),
  domain: z.string(),
  path: z.string(),
  name: z.string(),
  roles: z.array(z.string()),
  /**
   * Site-scoped feature toggles. Optional — older `/auth/me` responses
   * won't include it and the SPA must remain functional regardless.
   */
  features: SiteFeaturesSchema,
})

export type Site = z.infer<typeof SiteSchema>
